import React, { Component } from "react";
import { Container, Row, Col, Card, CardBody, Input, Button, Alert, Label, FormGroup } from "reactstrap";
import axios from "axios";
import baseURLs from "../../Constants";
import PlacesAutocomplete from "../../components/common/PlacesAutocomplete";
import "./fuel-order.css";

const baseURL = baseURLs.baseURL;

const VEHICLE_CATEGORIES = [
  "Sedan / Hatchback",
  "SUV / Crossover",
  "Bakkie / Pickup",
  "Minibus / Taxi",
  "Light Truck (1-3 ton)",
  "Heavy Truck (4-8 ton)",
  "Super-Link / Interlink",
  "Bus",
  "Construction / Plant",
  "Other",
];

class Section extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Form fields
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      deliveryAddress: "",
      deliveryLat: null,
      deliveryLng: null,
      vehicleCategory: "",
      vehicleMake: "",
      vehicleModel: "",
      vehicleYear: "",
      vehicleRegistration: "",
      tankCapacity: "",
      fuelType: "",
      litres: "",
      preferredDate: "",
      preferredTime: "",
      // Data
      fuelPrices: [],
      // UI
      step: 1, // 1=fuel, 2=location, 3=vehicle, 4=details, 5=quote, 6=confirmed
      quote: null,
      order: null,
      loading: false,
      error: "",
    };
  }

  componentDidMount() {
    this.fetchFuelPrices();
  }

  fetchFuelPrices() {
    axios
      .get(baseURL + "/fuel-prices")
      .then((res) => {
        this.setState({ fuelPrices: res.data || [] });
      })
      .catch(() => {
        this.setState({ error: "Could not load fuel prices. Please try again later." });
      });
  }

  validForm() {
    const { customerName, customerPhone, deliveryAddress, fuelType, litres, preferredDate } = this.state;
    return (
      customerName.length > 0 &&
      customerPhone.length >= 10 &&
      deliveryAddress.length > 0 &&
      fuelType.length > 0 &&
      Number(litres) > 0 &&
      preferredDate.length > 0
    );
  }

  vehicleComplete() {
    return this.state.vehicleCategory.length > 0;
  }

  canProceed = () => {
    const { step, fuelType, litres, preferredDate, deliveryAddress, customerName, customerPhone } = this.state;
    switch (step) {
      case 1: return fuelType.length > 0 && Number(litres) > 0 && preferredDate.length > 0;
      case 2: return deliveryAddress.length > 0;
      case 3: return true;
      case 4: return customerName.length > 0 && customerPhone.length >= 10;
      default: return false;
    }
  };

  nextStep = () => {
    this.setState({ step: this.state.step + 1, error: "" });
  };

  prevStep = () => {
    this.setState({ step: this.state.step - 1, error: "" });
  };

  getQuote = () => {
    if (!this.validForm()) {
      this.setState({ error: "Please fill in all required fields." });
      return;
    }
    this.setState({ loading: true, error: "" });

    axios
      .post(baseURL + "/fuel-quote", {
        customer_name: this.state.customerName,
        customer_email: this.state.customerEmail,
        customer_phone: this.state.customerPhone,
        delivery_address: this.state.deliveryAddress,
        delivery_lat: this.state.deliveryLat,
        delivery_lng: this.state.deliveryLng,
        vehicle_category: this.state.vehicleCategory,
        vehicle_make: this.state.vehicleMake,
        vehicle_model: this.state.vehicleModel,
        vehicle_year: this.state.vehicleYear ? Number(this.state.vehicleYear) : null,
        vehicle_registration: this.state.vehicleRegistration,
        tank_capacity_litres: this.state.tankCapacity ? Number(this.state.tankCapacity) : null,
        fuel_type: this.state.fuelType,
        litres: Number(this.state.litres),
        preferred_date: this.state.preferredDate,
        preferred_time: this.state.preferredTime,
      })
      .then((res) => {
        this.setState({ quote: res.data, step: 5, loading: false });
      })
      .catch((err) => {
        this.setState({
          error: err.response?.data?.error || "Failed to get quote. Please try again.",
          loading: false,
        });
      });
  };

  placeOrder = () => {
    this.setState({ loading: true, error: "" });

    axios
      .post(baseURL + "/fuel-order", {
        fuel_quote_id: this.state.quote?.id,
        customer_name: this.state.customerName,
        customer_email: this.state.customerEmail,
        customer_phone: this.state.customerPhone,
        delivery_address: this.state.deliveryAddress,
        delivery_lat: this.state.deliveryLat,
        delivery_lng: this.state.deliveryLng,
        vehicle_category: this.state.vehicleCategory,
        vehicle_make: this.state.vehicleMake,
        vehicle_model: this.state.vehicleModel,
        vehicle_year: this.state.vehicleYear ? Number(this.state.vehicleYear) : null,
        vehicle_registration: this.state.vehicleRegistration,
        tank_capacity_litres: this.state.tankCapacity ? Number(this.state.tankCapacity) : null,
        fuel_type: this.state.fuelType,
        litres: Number(this.state.litres),
        preferred_date: this.state.preferredDate,
        preferred_time: this.state.preferredTime,
      })
      .then((res) => {
        this.setState({ order: res.data, step: 6, loading: false });
      })
      .catch((err) => {
        this.setState({
          error: err.response?.data?.error || "Failed to place order. Please try again.",
          loading: false,
        });
      });
  };

  formatCents = (cents) => {
    return `R ${(cents / 100).toFixed(2)}`;
  };

  getFuelLabel = (fuelType) => {
    const found = this.state.fuelPrices.find((f) => f.fuel_type === fuelType);
    return found ? found.description : fuelType;
  };

  getVehicleSummary = () => {
    const { vehicleCategory, vehicleMake, vehicleModel, vehicleYear, vehicleRegistration } = this.state;
    const parts = [vehicleCategory, vehicleMake, vehicleModel, vehicleYear].filter(Boolean);
    const label = parts.join(" ").trim();
    return `${label || "No vehicle chosen yet"}${vehicleRegistration ? ` (${vehicleRegistration})` : ""}`;
  };

  getStepMeta = () => ([
    { id: 1, title: "Fuel" },
    { id: 2, title: "Location" },
    { id: 3, title: "Vehicle" },
    { id: 4, title: "Details" },
    { id: 5, title: "Review" },
    { id: 6, title: "Booked" },
  ]);

  renderInput = ({ label, type = "text", placeholder, value, onChange, min, max, children }) => (
    <FormGroup className="mb-0">
      <Label className="fuel-order-label">{label}</Label>
      <Input
        type={type}
        placeholder={placeholder}
        value={value}
        min={min}
        max={max}
        onChange={onChange}
        className="fuel-order-input"
      >
        {children}
      </Input>
    </FormGroup>
  );

  renderSectionHeader = (icon, title, subtitle) => (
    <div className="fuel-order-section-header">
      <div>
        <div className="fuel-order-section-title">{title}</div>
        {subtitle ? <p className="fuel-order-section-subtitle">{subtitle}</p> : null}
      </div>
      <div className="fuel-order-icon-badge">
        <i className={`mdi ${icon}`}></i>
      </div>
    </div>
  );

  renderVehicleCategories() {
    return (
      <div className="fuel-order-chip-grid">
        {VEHICLE_CATEGORIES.map((category) => (
          <button
            key={category}
            type="button"
            className={`fuel-order-chip ${this.state.vehicleCategory === category ? "active" : ""}`}
            onClick={() => this.setState({ vehicleCategory: category })}
          >
            <strong className="fuel-order-choice-title">{category}</strong>
          </button>
        ))}
      </div>
    );
  }

  renderFuelChoices() {
    const { fuelPrices, fuelType } = this.state;

    return (
      <div className="fuel-order-choice-grid">
        {fuelPrices.map((fuel) => (
          <button
            key={fuel.fuel_type}
            type="button"
            className={`fuel-order-choice ${fuelType === fuel.fuel_type ? "active" : ""}`}
            onClick={() => this.setState({ fuelType: fuel.fuel_type })}
          >
            <span className="fuel-order-choice-title">{fuel.description}</span>
            <div className="fuel-order-choice-price">{this.formatCents(fuel.price_per_litre_cents)}/L</div>
          </button>
        ))}
      </div>
    );
  }

  renderTimeChoices() {
    const slots = [
      { value: "", label: "Any time" },
      { value: "06:00-09:00", label: "06:00 - 09:00" },
      { value: "09:00-12:00", label: "09:00 - 12:00" },
      { value: "12:00-15:00", label: "12:00 - 15:00" },
      { value: "15:00-18:00", label: "15:00 - 18:00" },
    ];

    return (
      <div className="fuel-order-chip-grid">
        {slots.map((slot) => (
          <button
            key={slot.label}
            type="button"
            className={`fuel-order-chip ${this.state.preferredTime === slot.value ? "active" : ""}`}
            onClick={() => this.setState({ preferredTime: slot.value })}
          >
            <strong className="fuel-order-choice-title">{slot.label}</strong>
          </button>
        ))}
      </div>
    );
  }

  renderSidebar() {
    const { quote, order, fuelType, litres, preferredDate, preferredTime, customerName, deliveryAddress } = this.state;
    const currentTotal = quote?.total_cents || order?.total_cents || 0;

    return (
      <div className="fuel-order-sidebar">
        <div className="fuel-order-sidebar-card">
          <div className="fuel-order-sidebar-title">Order summary</div>
          <div className="fuel-order-sidebar-kpi">
            <strong>{currentTotal > 0 ? this.formatCents(currentTotal) : "Ready in seconds"}</strong>
            <span>{currentTotal > 0 ? "Current total" : "Complete the form to get a quote."}</span>
          </div>
          <div className="fuel-order-detail-list">
            <div className="fuel-order-detail-row">
              <span className="fuel-order-detail-label">Fuel request</span>
              <span className="fuel-order-detail-value">{fuelType ? `${this.getFuelLabel(fuelType)}${litres ? ` · ${litres}L` : ""}` : "Choose fuel type"}</span>
            </div>
            <div className="fuel-order-detail-row">
              <span className="fuel-order-detail-label">Delivery slot</span>
              <span className="fuel-order-detail-value">{preferredDate ? `${preferredDate}${preferredTime ? ` · ${preferredTime}` : " · Flexible"}` : "Pick a date"}</span>
            </div>
            <div className="fuel-order-detail-row">
              <span className="fuel-order-detail-label">Destination</span>
              <span className="fuel-order-detail-value">{deliveryAddress || "Add delivery address"}</span>
            </div>
            <div className="fuel-order-detail-row">
              <span className="fuel-order-detail-label">Vehicle</span>
              <span className="fuel-order-detail-value">{this.getVehicleSummary()}</span>
            </div>
            <div className="fuel-order-detail-row">
              <span className="fuel-order-detail-label">Customer</span>
              <span className="fuel-order-detail-value">{customerName || "Pending"}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderFormStep() {
    const { step, fuelPrices, loading } = this.state;

    const stepContent = () => {
      switch (step) {
        case 1:
          return (
            <div className="fuel-order-section">
              {this.renderSectionHeader("mdi-gas-station-outline", "Fuel request", null)}
              <div className="mb-3">
                <Label className="fuel-order-label">Fuel type *</Label>
                {fuelPrices.length > 0 ? this.renderFuelChoices() : <p className="fuel-order-muted mb-0">Loading active fuel prices...</p>}
              </div>
              <div className="fuel-order-grid two mt-3">
                {this.renderInput({
                  label: "Litres required *",
                  type: "number",
                  min: "1",
                  max: "10000",
                  placeholder: "e.g. 50",
                  value: this.state.litres,
                  onChange: (e) => this.setState({ litres: e.target.value }),
                })}
                {this.renderInput({
                  label: "Preferred date *",
                  type: "date",
                  min: new Date().toISOString().split("T")[0],
                  value: this.state.preferredDate,
                  onChange: (e) => this.setState({ preferredDate: e.target.value }),
                })}
              </div>
              <div className="mt-3">
                <Label className="fuel-order-label">Preferred time</Label>
                {this.renderTimeChoices()}
              </div>
            </div>
          );
        case 2:
          return (
            <div className="fuel-order-section">
              {this.renderSectionHeader("mdi-map-marker-outline", "Delivery location", null)}
              <FormGroup className="mb-0">
                <Label className="fuel-order-label">Delivery address *</Label>
                <PlacesAutocomplete
                  type="text"
                  className="fuel-order-address"
                  placeholder={this.state.deliveryAddress || "Search for your address..."}
                  onPlaceSelected={(place) => {
                    const lat = place.geometry?.location?.lat();
                    const lng = place.geometry?.location?.lng();
                    this.setState({
                      deliveryAddress: place.formatted_address || place.name || "",
                      deliveryLat: lat || null,
                      deliveryLng: lng || null,
                    });
                  }}
                />
              </FormGroup>
              {this.state.deliveryAddress && <p className="fuel-order-muted small mb-0 mt-3">Pinned location: {this.state.deliveryAddress}</p>}
            </div>
          );
        case 3:
          return (
            <div className="fuel-order-section">
              {this.renderSectionHeader("mdi-truck-outline", "Vehicle profile", "Optional for quote \u2014 required to confirm order")}
              <div className="mb-3">
                <Label className="fuel-order-label">Vehicle category</Label>
                {this.renderVehicleCategories()}
              </div>
              <div className="fuel-order-grid three mt-3">
                {this.renderInput({
                  label: "Make",
                  placeholder: "e.g. Toyota",
                  value: this.state.vehicleMake,
                  onChange: (e) => this.setState({ vehicleMake: e.target.value }),
                })}
                {this.renderInput({
                  label: "Model",
                  placeholder: "e.g. Hilux",
                  value: this.state.vehicleModel,
                  onChange: (e) => this.setState({ vehicleModel: e.target.value }),
                })}
                {this.renderInput({
                  label: "Year",
                  type: "number",
                  min: "1980",
                  max: `${new Date().getFullYear() + 1}`,
                  placeholder: "e.g. 2022",
                  value: this.state.vehicleYear,
                  onChange: (e) => this.setState({ vehicleYear: e.target.value }),
                })}
              </div>
              <div className="fuel-order-grid two mt-3">
                {this.renderInput({
                  label: "Registration number",
                  placeholder: "e.g. CA 123-456",
                  value: this.state.vehicleRegistration,
                  onChange: (e) => this.setState({ vehicleRegistration: e.target.value }),
                })}
                {this.renderInput({
                  label: "Tank capacity (litres)",
                  type: "number",
                  min: "1",
                  max: "5000",
                  placeholder: "e.g. 60",
                  value: this.state.tankCapacity,
                  onChange: (e) => this.setState({ tankCapacity: e.target.value }),
                })}
              </div>
            </div>
          );
        case 4:
          return (
            <div className="fuel-order-section">
              {this.renderSectionHeader("mdi-account-circle-outline", "Customer details", null)}
              <div className="fuel-order-grid two">
                {this.renderInput({
                  label: "Full name *",
                  placeholder: "e.g. John Doe",
                  value: this.state.customerName,
                  onChange: (e) => this.setState({ customerName: e.target.value }),
                })}
                {this.renderInput({
                  label: "Phone number *",
                  type: "tel",
                  placeholder: "e.g. 0712345678",
                  value: this.state.customerPhone,
                  onChange: (e) => this.setState({ customerPhone: e.target.value }),
                })}
              </div>
              <div className="fuel-order-grid mt-3">
                {this.renderInput({
                  label: "Email",
                  type: "email",
                  placeholder: "e.g. john@example.com",
                  value: this.state.customerEmail,
                  onChange: (e) => this.setState({ customerEmail: e.target.value }),
                })}
              </div>
            </div>
          );
        default:
          return null;
      }
    };

    return (
      <Card className="fuel-order-card fuel-order-surface">
        <CardBody>
          {stepContent()}

          {this.state.error && <Alert color="danger" className="mt-3 mb-0">{this.state.error}</Alert>}

          <div className="fuel-order-actions">
            {step > 1 && (
              <Button color="secondary" className="fuel-order-btn-secondary" onClick={this.prevStep}>
                Back
              </Button>
            )}
            {step === 3 && !this.vehicleComplete() && (
              <Button color="secondary" className="fuel-order-btn-secondary" onClick={this.nextStep}>
                Skip
              </Button>
            )}
            {step < 4 ? (
              <Button
                color="primary"
                size="lg"
                className="fuel-order-btn-primary"
                disabled={!this.canProceed()}
                onClick={this.nextStep}
              >
                Continue
              </Button>
            ) : (
              <Button
                color="primary"
                size="lg"
                className="fuel-order-btn-primary"
                disabled={loading || !this.canProceed()}
                onClick={this.getQuote}
              >
                {loading ? "Generating quote..." : "Generate instant quote"}
              </Button>
            )}
          </div>
        </CardBody>
      </Card>
    );
  }

  renderQuote() {
    const { quote, loading } = this.state;
    if (!quote) return null;

    return (
      <Card className="fuel-order-card fuel-order-surface">
        <CardBody>
          {this.renderSectionHeader("mdi-receipt-text-outline", "Fuel quote", null)}
          <div className="fuel-order-callout">
            Quote reference <strong>{quote.quote_number}</strong>
            {quote.expires_at && <> · Valid until <strong>{new Date(quote.expires_at).toLocaleString("en-ZA")}</strong></>}
          </div>

          <div className="fuel-order-quote-breakdown">
            <div className="fuel-order-breakdown-row">
              <div>
                <strong>{this.getFuelLabel(quote.fuel_type)}</strong>
                <div className="fuel-order-muted small">{quote.litres} litres at {this.formatCents(quote.price_per_litre_cents)}/L</div>
              </div>
              <strong>{this.formatCents(quote.fuel_cost_cents)}</strong>
            </div>
            <div className="fuel-order-breakdown-row">
              <div>
                <strong>Delivery fee</strong>
              </div>
              <strong>{this.formatCents(quote.delivery_fee_cents)}</strong>
            </div>
            <div className="fuel-order-breakdown-row fuel-order-total-row">
              <div>
                <strong>Total payable</strong>
              </div>
              <strong>{this.formatCents(quote.total_cents)}</strong>
            </div>
          </div>

          <div className="fuel-order-section">
            {this.renderSectionHeader("mdi-clipboard-text-outline", "Order summary", null)}
            <div className="fuel-order-detail-list">
              <div className="fuel-order-detail-row">
                <span className="fuel-order-detail-label">Delivery address</span>
                <span className="fuel-order-detail-value">{this.state.deliveryAddress}</span>
              </div>
              <div className="fuel-order-detail-row">
                <span className="fuel-order-detail-label">Vehicle</span>
                <span className="fuel-order-detail-value">{this.getVehicleSummary()}</span>
              </div>
              <div className="fuel-order-detail-row">
                <span className="fuel-order-detail-label">Preferred slot</span>
                <span className="fuel-order-detail-value">{this.state.preferredDate} {this.state.preferredTime || "· Flexible"}</span>
              </div>
              <div className="fuel-order-detail-row">
                <span className="fuel-order-detail-label">Contact</span>
                <span className="fuel-order-detail-value">{this.state.customerName} · {this.state.customerPhone}</span>
              </div>
            </div>
          </div>

          {!this.vehicleComplete() && (
            <Alert color="warning" className="mt-3 mb-0">
              <i className="mdi mdi-alert-outline me-1"></i>
              Vehicle profile is required to confirm the order. <a href="#!" onClick={(e) => { e.preventDefault(); this.setState({ step: 3, quote: null }); }}>Go back and complete it</a>.
            </Alert>
          )}

          {this.state.error && <Alert color="danger" className="mt-3 mb-0">{this.state.error}</Alert>}

          <div className="fuel-order-actions">
            <Button color="secondary" className="fuel-order-btn-secondary" onClick={() => this.setState({ step: 1, quote: null })}>
              Refine request
            </Button>
            {this.vehicleComplete() ? (
              <Button color="success" size="lg" className="fuel-order-btn-success" disabled={loading} onClick={this.placeOrder}>
                {loading ? "Confirming order..." : "Confirm fuel order"}
              </Button>
            ) : (
              <Button color="warning" size="lg" className="fuel-order-btn-primary" onClick={() => this.setState({ step: 3, quote: null })}>
                Complete vehicle profile
              </Button>
            )}
          </div>
        </CardBody>
      </Card>
    );
  }

  renderConfirmation() {
    const { order } = this.state;
    if (!order) return null;

    return (
      <Card className="fuel-order-card fuel-order-surface">
        <CardBody className="fuel-order-confirmation">
          <div className="fuel-order-success-orb">
            <i className="mdi mdi-check-bold"></i>
          </div>
          <h3 className="mb-3">Fuel order confirmed</h3>
          <div className="fuel-order-order-number">Order number: {order.order_number}</div>
          <p className="fuel-order-muted mb-0">
            QikTruck operations will contact <strong>{order.customer_phone}</strong> to confirm delivery logistics for <strong>{order.preferred_date}</strong>.
          </p>

          <div className="fuel-order-mini-grid">
            <div className="fuel-order-mini-card">
              <strong>Fuel</strong>
              <span>{this.getFuelLabel(order.fuel_type)} · {order.litres}L</span>
            </div>
            <div className="fuel-order-mini-card">
              <strong>Total</strong>
              <span>{this.formatCents(order.total_cents)}</span>
            </div>
            <div className="fuel-order-mini-card">
              <strong>Destination</strong>
              <span>{order.delivery_address}</span>
            </div>
          </div>

          <div className="fuel-order-actions justify-content-center">
            <Button color="primary" className="fuel-order-btn-primary" onClick={() => this.setState({ step: 1, quote: null, order: null })}>
              Place another order
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  render() {
    const { step } = this.state;

    return (
      <React.Fragment>
        <div className="fuel-order-page">
          <section className="fuel-order-hero">
            <Container className="fuel-order-shell">
              <div className="fuel-order-hero-card">
                <Row className="align-items-center">
                  <Col lg={8}>
                    <div className="fuel-order-kicker">
                      <i className="mdi mdi-fire"></i>
                      QikTruck fuel operations
                    </div>
                    <h1 className="fuel-order-hero-title">
                      Fuel ordering that feels <span className="fuel-order-hero-accent">operational, fast, and premium.</span>
                    </h1>
                    <p className="fuel-order-hero-copy">
                      Order fuel, get a quote, and confirm in a few steps.
                    </p>
                  </Col>
                </Row>
              </div>
            </Container>
          </section>

          <section className="fuel-order-main">
            <Container className="fuel-order-shell">
              <div className="fuel-order-steps">
                {this.getStepMeta().map((item) => (
                  <div
                    key={item.id}
                    className={`fuel-order-step ${step === item.id ? "active" : ""} ${step > item.id ? "done" : ""}`}
                    onClick={() => { if (step > item.id) this.setState({ step: item.id, error: "", quote: item.id <= 4 ? null : this.state.quote }); }}
                  >
                    <div className="fuel-order-step-number">{step > item.id ? "\u2713" : item.id}</div>
                    <div className="fuel-order-step-title">{item.title}</div>
                  </div>
                ))}
              </div>

              <Row>
                <Col xl={9}>
                  {step >= 1 && step <= 4 && this.renderFormStep()}
                  {step === 5 && this.renderQuote()}
                  {step === 6 && this.renderConfirmation()}
                </Col>
                <Col xl={3}>
                  {this.renderSidebar()}
                </Col>
              </Row>
            </Container>
          </section>
        </div>
      </React.Fragment>
    );
  }
}

export default Section;
